import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import type { ConditionGroup } from '@/lib/templates/conditions';
import {
  createTemplateScreen,
  updateTemplateScreen,
} from '@/modules/templates/services/templates.service';
import type {
  TemplateScreen,
  TemplateScreenUpdateInput,
  TemplateField,
} from '@/modules/templates/types/templates.types';
import { syncScreenSave } from '../lib/sync-template-uilm';
import { useToast } from '@/hooks/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ChevronDown,
  ChevronUp,
  Info,
  Loader2,
  Sparkles,
  Wand2,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ConditionEditor, type AvailableField } from './condition-editor';

const screenSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  type: z.enum(['standard', 'signatory', 'dynamic']).default('standard'),
  // Dynamic screen configuration
  dynamicPrompt: z.string().optional(),
  dynamicMaxFields: z.number().int().min(1).max(20).optional().default(5),
  // Apply Standards feature
  enableApplyStandards: z.boolean().optional().default(false),
});

type ScreenFormInput = z.input<typeof screenSchema>;
type ScreenFormData = z.output<typeof screenSchema>;

// Interface for available variables from previous screens
interface AvailableVariable {
  name: string;
  screenTitle: string;
  source: 'field' | 'enrichment';
}

interface ScreenEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  /** Slug of the parent template — required for UILM key generation. */
  templateSlug: string;
  /**
   * Parent template's organizationId. UILM sync only runs for system
   * templates (organizationId is null/empty). Org-owned templates skip
   * the sync entirely.
   */
  templateOrganizationId?: string | null;
  screen: TemplateScreen | null;
  allScreens?: Array<TemplateScreen & { fields?: TemplateField[] }>; // All screens to compute available variables
  onSaved: () => void;
}

export function ScreenEditor({
  open,
  onOpenChange,
  templateId,
  templateSlug,
  templateOrganizationId,
  screen,
  allScreens = [],
  onSaved,
}: ScreenEditorProps) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dynamicConfigExpanded, setDynamicConfigExpanded] = useState(false);
  const [conditions, setConditions] = useState<ConditionGroup | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ScreenFormInput, unknown, ScreenFormData>({
    resolver: zodResolver(screenSchema),
    defaultValues: {
      title: '',
      description: '',
      type: 'standard',
      dynamicPrompt: '',
      dynamicMaxFields: 5,
      enableApplyStandards: false,
    },
  });

  const screenType = watch('type');

  // Compute available variables from previous screens
  const availableVariables = useMemo(() => {
    const variables: AvailableVariable[] = [];

    // Get the order of current screen (or last position if new screen)
    const currentOrder = screen?.order ?? allScreens.length;

    // Get previous screens
    const previousScreens = allScreens
      .filter((s) => s.order < currentOrder)
      .sort((a, b) => a.order - b.order);

    previousScreens.forEach((prevScreen) => {
      // Add field variables
      if (prevScreen.fields) {
        prevScreen.fields.forEach((field) => {
          variables.push({
            name: field.name,
            screenTitle: prevScreen.title,
            source: 'field',
          });
        });
      }

      // Add enrichment context variables from AI output schema
      if (prevScreen.aiOutputSchema) {
        try {
          const schema = JSON.parse(prevScreen.aiOutputSchema);
          if (schema.properties) {
            Object.keys(schema.properties).forEach((key) => {
              variables.push({
                name: key,
                screenTitle: prevScreen.title,
                source: 'enrichment',
              });
            });
          }
        } catch {
          // Ignore parse errors
        }
      }
    });

    return variables;
  }, [allScreens, screen?.order]);

  // Compute available fields for condition editor (from previous screens)
  const availableFieldsForConditions: AvailableField[] = useMemo(() => {
    const fields: AvailableField[] = [];

    // Get the order of current screen (or last position if new screen)
    const currentOrder = screen?.order ?? allScreens.length;

    // Get previous screens
    const previousScreens = allScreens
      .filter((s) => s.order < currentOrder)
      .sort((a, b) => a.order - b.order);

    previousScreens.forEach((prevScreen) => {
      // Add fields from each previous screen
      if (prevScreen.fields) {
        prevScreen.fields.forEach((field) => {
          fields.push({
            name: field.name,
            label: field.label,
            screenTitle: prevScreen.title,
            type: field.type,
          });
        });
      }
    });

    return fields;
  }, [allScreens, screen?.order]);

  // Reset form when dialog opens/closes or screen changes
  useEffect(() => {
    if (open) {
      reset({
        title: screen?.title || '',
        description: screen?.description || '',
        type: screen?.type || 'standard',
        dynamicPrompt: screen?.dynamicPrompt || '',
        dynamicMaxFields: screen?.dynamicMaxFields ?? 5,
        enableApplyStandards: screen?.enableApplyStandards ?? false,
      });
      setError(null);
      // Expand dynamic config section if it's a dynamic screen
      setDynamicConfigExpanded(screen?.type === 'dynamic');
      // Load conditions from screen
      const screenConditions = screen?.conditions;
      if (screenConditions) {
        try {
          setConditions(
            typeof screenConditions === 'string' ? JSON.parse(screenConditions) : screenConditions
          );
        } catch {
          setConditions(null);
        }
      } else {
        setConditions(null);
      }
    }
  }, [open, screen, reset]);

  // Insert variable into dynamic prompt at cursor position
  const insertVariable = (varName: string) => {
    const currentPrompt = watch('dynamicPrompt') || '';
    setValue('dynamicPrompt', currentPrompt + `{{${varName}}}`, {
      shouldDirty: true,
    });
  };

  const onSubmit = async (data: ScreenFormData) => {
    setSaving(true);
    setError(null);

    try {
      // UILM keys are populated by syncScreenSave after the UDS write.
      const udsInput = {
        Title: data.title,
        Description: data.description,
        Type: data.type,
        EnableApplyStandards: data.enableApplyStandards ?? false,
        Conditions: conditions ? JSON.stringify(conditions) : null,
        DynamicPrompt: data.type === 'dynamic' ? data.dynamicPrompt || null : null,
        DynamicMaxFields: data.type === 'dynamic' ? data.dynamicMaxFields ?? 5 : null,
      } satisfies TemplateScreenUpdateInput;

      let savedScreenId: string | null;
      if (screen) {
        await updateTemplateScreen(screen.id, udsInput);
        savedScreenId = screen.id;
      } else {
        const nextOrder =
          allScreens.reduce((maxOrder, item) => Math.max(maxOrder, item.order), -1) + 1;
        const createResult = await createTemplateScreen({
          ...udsInput,
          TemplateId: templateId,
          Order: nextOrder,
        });
        savedScreenId = createResult.insertTemplateScreens.itemId;
        if (!savedScreenId) throw new Error('Failed to create screen');
      }

      // UILM sync — system templates only. Non-fatal: a sync failure is
      // surfaced as a "deferred" toast and the screen save still succeeds.
      const isSystemTemplate = !templateOrganizationId;
      if (isSystemTemplate && savedScreenId) {
        try {
          await syncScreenSave({
            templateSlug,
            screenId: savedScreenId,
            title: data.title,
            description: data.description,
          });
        } catch (uilmErr) {
          console.warn('UILM sync deferred for screen:', uilmErr);
          toast({
            variant: 'default',
            title: 'Translation sync deferred',
            description:
              'Screen was saved, but translation keys could not be pushed to UILM. Open the Translations tab to retry.',
          });
        }
      }

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save screen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>{screen ? 'Edit Screen' : 'Add Screen'}</DialogTitle>
            <DialogDescription>
              {screen
                ? 'Update the screen title and description.'
                : 'Create a new screen for the form wizard.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input id="title" placeholder="e.g., Company Information" {...register('title')} />
              {errors.title && <p className="text-sm text-destructive">{errors.title.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Optional helper text for this step"
                {...register('description')}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Screen Type</Label>
              <Select
                value={watch('type')}
                onValueChange={(value) => {
                  setValue('type', value as 'standard' | 'signatory' | 'dynamic');
                  if (value === 'dynamic') {
                    setDynamicConfigExpanded(true);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select screen type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="signatory">Signatory Information</SelectItem>
                  <SelectItem value="dynamic">
                    <div className="flex items-center gap-2">
                      <Wand2 className="h-4 w-4 text-selise-blue" />
                      <span>Dynamic AI Screen</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-globe-grey">
                {screenType === 'signatory'
                  ? 'Special screen for collecting signatory information (name, email, title, etc.)'
                  : screenType === 'dynamic'
                    ? 'AI-generated form fields based on context from previous steps'
                    : 'Regular form screen with custom fields'}
              </p>
              {errors.type && <p className="text-sm text-destructive">{errors.type.message}</p>}
            </div>


            {/* Apply Standards Configuration */}
            <div className="flex items-start gap-3 p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors">
              <input
                type="checkbox"
                id="enableApplyStandards"
                {...register('enableApplyStandards')}
                className="mt-1 h-4 w-4 rounded border-border text-selise-blue focus:ring-selise-blue"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" />
                  <Label htmlFor="enableApplyStandards" className="font-medium cursor-pointer">
                    Enable &quot;Apply Standards&quot; Button
                  </Label>
                  {watch('enableApplyStandards') && (
                    <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                      Enabled
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-globe-grey mt-1">
                  Show a button that auto-fills fields with AI suggestions from enrichment context.
                  Fields must have &quot;AI Suggestion&quot; enabled and configured to be
                  auto-filled.
                </p>
              </div>
            </div>

            {/* Conditional Visibility */}
            <ConditionEditor
              value={conditions}
              onChange={setConditions}
              availableFields={availableFieldsForConditions}
              label="Conditional Visibility"
              description="Show this screen only when specific conditions are met based on previous form responses."
            />

            {/* Dynamic Screen Configuration */}
            {screenType === 'dynamic' && (
              <div className="border border-selise-blue/30 rounded-lg overflow-hidden bg-selise-blue/5">
                <button
                  type="button"
                  onClick={() => setDynamicConfigExpanded(!dynamicConfigExpanded)}
                  className="w-full flex items-center justify-between p-3 hover:bg-selise-blue/10 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Wand2 className="h-4 w-4 text-selise-blue" />
                    <span className="font-medium text-sm text-[hsl(var(--fg))]">
                      Dynamic Screen Configuration
                    </span>
                    {watch('dynamicPrompt') && (
                      <Badge
                        variant="secondary"
                        className="text-xs bg-selise-blue/20 text-selise-blue"
                      >
                        Configured
                      </Badge>
                    )}
                  </div>
                  {dynamicConfigExpanded ? (
                    <ChevronUp className="h-4 w-4 text-globe-grey" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-globe-grey" />
                  )}
                </button>

                {dynamicConfigExpanded && (
                  <div className="p-4 space-y-4 border-t border-selise-blue/20">
                    {/* Info Box */}
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-selise-blue/10">
                      <Info className="h-4 w-4 text-selise-blue mt-0.5 shrink-0" />
                      <p className="text-xs text-[hsl(var(--fg))]">
                        This screen will generate form fields dynamically using AI based on your
                        prompt. Use{' '}
                        <code className="px-1 py-0.5 bg-[hsl(var(--bg))] rounded text-selise-blue">
                          {'{{variableName}}'}
                        </code>{' '}
                        to reference values from previous steps.
                      </p>
                    </div>

                    {/* AI Prompt */}
                    <div className="space-y-2">
                      <Label htmlFor="dynamicPrompt" className="text-sm font-medium">
                        AI Prompt for Field Generation *
                      </Label>
                      <textarea
                        id="dynamicPrompt"
                        {...register('dynamicPrompt')}
                        placeholder="e.g., Based on {{jurisdiction}}, what additional questions should we ask for drafting an NDA for {{purpose}}?"
                        className="w-full min-h-25 p-3 rounded-md border border-border bg-[hsl(var(--bg))] text-sm resize-y focus:outline-none focus:ring-2 focus:ring-selise-blue focus:border-transparent"
                      />
                      <p className="text-xs text-globe-grey">
                        The AI will generate relevant form fields based on this prompt and context
                        from previous steps.
                      </p>
                    </div>

                    {/* Max Fields */}
                    <div className="space-y-2">
                      <Label htmlFor="dynamicMaxFields" className="text-sm font-medium">
                        Maximum Fields to Generate
                      </Label>
                      <Input
                        id="dynamicMaxFields"
                        type="number"
                        min={1}
                        max={20}
                        {...register('dynamicMaxFields', {
                          valueAsNumber: true,
                        })}
                        className="w-32"
                      />
                      <p className="text-xs text-globe-grey">
                        Limit the number of fields the AI can generate (1-20).
                      </p>
                    </div>

                    {/* Note about AI settings location */}
                    <div className="rounded-lg border border-selise-blue/20 bg-selise-blue/5 p-3">
                      <p className="text-xs text-globe-grey">
                        <span className="font-medium text-[hsl(var(--fg))]">
                          AI Model & System Prompt:
                        </span>{' '}
                        Configure the AI model and system prompt in{' '}
                        <a
                          href="/en/admin/settings"
                          className="text-selise-blue underline hover:no-underline"
                        >
                          System Settings
                        </a>
                        . These settings apply to all Dynamic AI Screens.
                      </p>
                    </div>

                    {/* Available Variables */}
                    {availableVariables.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-globe-grey uppercase tracking-wider">
                          Available Variables from Previous Steps
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {availableVariables.map((v, idx) => (
                            <Badge
                              key={idx}
                              variant="outline"
                              className="cursor-pointer hover:bg-selise-blue/10 hover:text-selise-blue hover:border-selise-blue transition-colors"
                              onClick={() => insertVariable(v.name)}
                            >
                              {v.source === 'enrichment' ? (
                                <Sparkles className="h-3 w-3 mr-1 text-selise-blue" />
                              ) : null}
                              {v.name}
                              <span className="ml-1 text-[10px] opacity-50">({v.screenTitle})</span>
                            </Badge>
                          ))}
                        </div>
                        <p className="text-xs text-globe-grey italic">
                          Click a variable to insert it into your prompt.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-selise-blue hover:bg-[hsl(var(--oxford-blue))] text-[hsl(var(--white))] hover:text-[hsl(var(--white))]"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {screen ? 'Save Changes' : 'Create Screen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
